import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Session-safe token storage (survives page refresh within same tab)
let accessToken = sessionStorage.getItem('_at') || null;
let refreshToken = sessionStorage.getItem('_rt') || null;

export const setTokens = (access, refresh) => {
  accessToken = access;
  refreshToken = refresh;
  sessionStorage.setItem('_at', access);
  sessionStorage.setItem('_rt', refresh);
};

export const clearTokens = () => {
  accessToken = null;
  refreshToken = null;
  sessionStorage.removeItem('_at');
  sessionStorage.removeItem('_rt');
};

export const getAccessToken = () => accessToken;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Add auth token to every request
api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Handle auth errors with token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && refreshToken) {
      originalRequest._retry = true;
      try {
        const res = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: refreshToken });
        accessToken = res.data.access_token;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getRecentBookings = () => api.get('/dashboard/recent-bookings');
export const getRecentOrders = () => api.get('/dashboard/recent-orders');

// Rooms
export const getRooms = (status) => api.get('/rooms', { params: { status } });
export const getRoom = (id) => api.get(`/rooms/${id}`);
export const createRoom = (data) => api.post('/rooms', data);
export const updateRoom = (id, data) => api.put(`/rooms/${id}`, data);
export const deleteRoom = (id) => api.delete(`/rooms/${id}`);

// Guests
export const getGuests = (search) => api.get('/guests', { params: { search } });
export const getGuest = (id) => api.get(`/guests/${id}`);
export const createGuest = (data) => api.post('/guests', data);
export const updateGuest = (id, data) => api.put(`/guests/${id}`, data);
export const deleteGuest = (id) => api.delete(`/guests/${id}`);

// Bookings
export const getBookings = (status) => api.get('/bookings', { params: { status } });
export const getBooking = (id) => api.get(`/bookings/${id}`);
export const createBooking = (data) => api.post('/bookings', data);
export const createGroupBooking = (data) => api.post('/bookings/group', data);
export const updateBooking = (id, data) => api.put(`/bookings/${id}`, data);
export const checkIn = (id) => api.post(`/bookings/${id}/check-in`);
export const checkOut = (id) => api.post(`/bookings/${id}/check-out`);
export const cancelBooking = (id) => api.delete(`/bookings/${id}`);

// Payments
export const getPayments = (bookingId) => api.get('/payments', { params: { booking_id: bookingId } });
export const getPayment = (id) => api.get(`/payments/${id}`);
export const createPayment = (data) => api.post('/payments', data);

// Admin
export const adminDeleteBooking = (id) => api.delete(`/admin/bookings/${id}`);
export const adminDeletePayment = (id) => api.delete(`/admin/payments/${id}`);
export const adminCleanupOrphans = () => api.post('/admin/cleanup-orphans');
export const adminFullReset = () => api.post('/admin/full-reset', { confirm: 'RESET' });

// Bulk booking upload
export const downloadBulkTemplate = () => api.get('/bookings/bulk-template', { responseType: 'blob' });
export const uploadBulkBookings = (file, dryRun = true) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/bookings/bulk-upload?dry_run=${dryRun}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Invoices
export const getInvoices = () => api.get('/invoices');
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const getInvoiceByBooking = (bookingId) => api.get(`/invoices/by-booking/${bookingId}`);
export const regenerateInvoice = (bookingId) => api.post(`/bookings/${bookingId}/invoice`);

// Menu (F&B)
export const getMenuItems = (category, available) => api.get('/menu', { params: { category, available } });
export const getMenuItem = (id) => api.get(`/menu/${id}`);
export const createMenuItem = (data) => api.post('/menu', data);
export const updateMenuItem = (id, data) => api.put(`/menu/${id}`, data);
export const deleteMenuItem = (id) => api.delete(`/menu/${id}`);

// Orders (F&B)
export const getOrders = (status) => api.get('/orders', { params: { status } });
export const getOrder = (id) => api.get(`/orders/${id}`);
export const createOrder = (data) => api.post('/orders', data);
export const updateOrderStatus = (id, status) => api.put(`/orders/${id}/status`, { status });

// Staff
export const getStaff = () => api.get('/staff');
export const updateStaffRole = (id, role) => api.put(`/staff/${id}/role?role=${role}`);
export const deleteStaff = (id) => api.delete(`/staff/${id}`);

export const openProtectedFile = async (fileUrl) => {
  const res = await api.get(fileUrl.replace(/^\/api/, ''), { responseType: 'blob' });
  window.open(URL.createObjectURL(res.data), '_blank', 'noopener');
};

export default api;
